import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useRelationshipTypeCriteria_query$data,
  type useRelationshipTypeCriteria_query$key
} from '@/__generated__/useRelationshipTypeCriteria_query.graphql'
import { type useRelationshipTypeCriteriaMutation } from '@/__generated__/useRelationshipTypeCriteriaMutation.graphql'

type CommitAsyncFn = (relationshipTypeIds: string[]) => Promise<useRelationshipTypeCriteria_query$data>

export const useRelationshipTypeCriteria = (
  queryRef?: useRelationshipTypeCriteria_query$key | null | undefined
): [string[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useRelationshipTypeCriteria_query on Query {
        currentUser {
          relationshipTypeCriteria(orderBy: [RELATIONSHIP_TYPE_BY_RELATIONSHIP_TYPE_ID__SEQUENCE_ASC]) {
            nodes {
              relationshipTypeId
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () => map(queryData?.currentUser?.relationshipTypeCriteria.nodes, 'relationshipTypeId'),
    [queryData?.currentUser?.relationshipTypeCriteria.nodes]
  )
  const [commit, isPending] = useMutation<useRelationshipTypeCriteriaMutation>(graphql`
    mutation useRelationshipTypeCriteriaMutation($input: UpdateRelationshipTypeCriteriaInput!) {
      updateRelationshipTypeCriteria(input: $input) {
        query {
          ...useRelationshipTypeCriteria_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (relationshipTypeIds: string[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              relationshipTypeIds
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
